import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  useBind,
  useCancellablePromise,
  useSmartCaptcha,
  useSnackbar,
} from '../../../../../../hooks';
import { OneTimePinStepForm } from './one-time-pin-step-form';
import { yup } from '../../../../../../services/yup';
import { useOtpCodeValidation } from '../../../../../../hooks/use-otp-code-validation';
import {
  useActionsInProgress,
} from '../../../../../../graphql/preloader/actions/actions-in-progress';
import { IExternalStateProps, IStepsState, TOneTimePinValues } from '../../reset-password.models';
import { consumerApi, RecaptchaActions } from '../../../../../../rest/consumer';
import { useResetPasswordError } from '../../hooks/use-reset-password-error';
import { CaptchaType } from '../../../../../../graphql/configuration';
import {
  IFormImperativeHandleProps,
  IStepComponentProps,
  IStepperImperativeHandleProps,
} from '../../../../../common';
import { handleReCaptcha } from '../../../../../../utils';
import { CAPTCHA_CHECK_ERROR } from '../../../../../../constants';

export const OneTimePinStep = forwardRef<
IStepperImperativeHandleProps, IStepComponentProps>(({
  onGoNextSuccess,
  onGoNextFail,
  onGoNext,
  isNavigationAllowed,
  stepsState,
  externalState,
}, ref): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { oneTimePin, username: { username } } = stepsState as IStepsState;

  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();

  const { enqueueSnackbar } = useSnackbar();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const enqueueSnackbarBind = useBind(enqueueSnackbar);
  const onGoNextFailBind = useBind(onGoNextFail);
  const onGoNextSuccessBind = useBind(onGoNextSuccess);
  const isNavigationAllowedBind = useBind(isNavigationAllowed);
  const onGoNextBind = useBind(onGoNext);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);

  const otpCodeValidation = useOtpCodeValidation();

  const { time, start, status: timerStatus, captcha } = externalState as IExternalStateProps;

  const validationSchema = useMemo(() => yup.object({
    code: otpCodeValidation,
  }), [otpCodeValidation]);

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {},
  }));

  const defaultValues = useMemo(
    () => oneTimePin || {
      code: '',
    },
    [oneTimePin],
  );

  const handleAfterRequestCode = () => {
    enqueueSnackbarBind.current(
      <FormattedMessage id="resetPassword.successRequest" />,
      { variant: 'success' },
    );

    start(username);

    removeActionInProgressBind.current();
  };

  const { handleError } = useResetPasswordError();

  // SmartCaptcha
  const { smartToken, isVisible, handleSmartCaptcha, showCaptcha, clearToken, isReset, handleReset, hideCaptcha } = useSmartCaptcha();

  // This function is called when the SmartCaptcha challenge is hidden (either passed or closed)
  const handleChallengeHidden = useCallback((): void => {
    if (!smartToken) {
      clearToken();
      hideCaptcha();
    }
  }, [smartToken]);

  const handlePhoneSend = useCallback(async (token: string, user: string) => {
    await makeCancellablePromiseBind.current(
      consumerApi.sendPinCodeCaptcha({ username: user }, token, captcha.type));

    handleAfterRequestCode();
  }, []);

  const handleSendError = useCallback((error: any): void => {
    removeActionInProgressBind.current();

    if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
      return;
    }

    handleError(error);
    onGoNextFailBind.current();
  }, [removeActionInProgressBind, handleError, onGoNextFailBind, CancelledPromiseOnUnmountErrorBind]);

  const handleRequestCode = useCallback(async () => {
    if (!isNavigationAllowedBind.current) {
      return;
    }

    // SmartCaptcha - Invisible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && !captcha.isVisibleCaptcha) {
      showCaptcha(async (token: string) => {
        addActionInProgressBind.current();

        try {
          await handlePhoneSend(token, username);
          clearToken();
          handleReset(true);
        } catch (error: any) {
          handleSendError(error);
          handleReset(true);
        }
      });

      return;
    }

    // Smart Captcha - Visible mode
    if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.smartCaptcha && captcha.isVisibleCaptcha) {
      if (!smartToken) {
        enqueueSnackbarBind.current(CAPTCHA_CHECK_ERROR, { variant: 'error' });
        return;
      }

      addActionInProgressBind.current();

      try {
        await handlePhoneSend(smartToken, username);
        clearToken();
        handleReset(true);
      } catch (error: any) {
        handleSendError(error);
        handleReset(true);
      }

      return;
    }

    // For non-SmartCaptcha flows, proceed normally
    addActionInProgressBind.current();

    try {
      if (captcha && captcha?.isEnabled && captcha.type === CaptchaType.reCaptcha) {
        handleReCaptcha(
          (token: string) => handlePhoneSend(token, username),
          handleSendError,
          RecaptchaActions.resetPassword,
        );
      } else {
        await makeCancellablePromiseBind.current(consumerApi.sendPinCode({ username }));

        handleAfterRequestCode();
      }
    } catch (error: any) {
      handleSendError(error);
    }
  }, [
    onGoNextBind,
    onGoNextSuccessBind,
    isNavigationAllowedBind,
    enqueueSnackbarBind,
    makeCancellablePromiseBind,
    addActionInProgressBind,
    captcha,
    handleReCaptcha,
    handleAfterRequestCode,
    handleSendError,
    showCaptcha,
    handlePhoneSend,
    username,
    handleReset,
  ]);

  const handleSubmit = useCallback(async (formValues: TOneTimePinValues) => {
    if (!isNavigationAllowedBind.current) {
      return;
    }

    onGoNextBind.current();
    addActionInProgressBind.current();

    try {
      await makeCancellablePromiseBind.current(consumerApi.confirmPinCode({
        ...formValues,
        username,
      }));

      removeActionInProgressBind.current();
      onGoNextSuccessBind.current(formValues);
    } catch (error: any) {
      removeActionInProgressBind.current();

      if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
        return;
      }

      if (error.response) {
        const { status } = error.response;

        switch (status) {
          case 400:
            enqueueSnackbarBind.current(
              <FormattedMessage
                id="resetPassword.oneTimePinStep.errorRequest"
              />,
              { variant: 'error' },
            );
            break;
          default:
            break;
        }
      }

      onGoNextFailBind.current();
    }
  }, [
    start,
    enqueueSnackbarBind,
    onGoNextFailBind,
    onGoNextSuccessBind,
    onGoNextBind,
    isNavigationAllowedBind,
    makeCancellablePromiseBind,
    CancelledPromiseOnUnmountErrorBind,
    addActionInProgressBind,
    removeActionInProgressBind,
  ]);

  return (
    <>
      <OneTimePinStepForm
        ref={formRef}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        requestCodeTime={time}
        timerStatus={timerStatus}
        onRequestCode={handleRequestCode}
        captchaType={captcha?.type}
        onSuccess={handleSmartCaptcha}
        isVisible={isVisible}
        captcha={captcha}
        isResetCaptcha={isReset}
        onChallengeHidden={handleChallengeHidden}
      />
    </>
  );
});
